import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  Bell,
  CheckCircle2,
  Clock,
  ExternalLink,
  History,
  Info,
  Loader2,
  Phone,
  RefreshCw,
  Send,
  Sparkles,
  UserCheck,
  Users,
  X,
} from 'lucide-react';

import ModalShell from './ModalShell';
import { API_URL } from '../config';

const clientAppRequest = async (path, options = {}) => {
  const response = await fetch(`${API_URL}/api/client-catalog-admin/v1${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(options.body ? { 'Content-Type': 'application/json' } : {}),
      ...(options.headers || {}),
    },
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const detail = payload?.detail;
    throw new Error(
      typeof detail === 'string'
        ? detail
        : detail?.message || 'Не удалось выполнить действие'
    );
  }
  return payload;
};

const SEGMENTS = [
  {
    id: 'all',
    title: 'Все клиенты',
    caption: 'Массовая рассылка всем физлицам в базе',
    icon: Users,
    color: 'blue',
  },
  {
    id: 'inactive_30',
    title: 'Отток 30+ дней',
    caption: 'Клиенты, у которых не было заказов больше месяца',
    icon: Clock,
    color: 'amber',
  },
  {
    id: 'inactive_60',
    title: 'Отток 60+ дней',
    caption: 'Глубокий отток: не заказывали 2 месяца и более',
    icon: AlertCircle,
    color: 'rose',
  },
  {
    id: 'with_bonuses',
    title: 'Есть бонусы',
    caption: 'Клиенты с положительным балансом бонусных рублей',
    icon: Sparkles,
    color: 'violet',
  },
  {
    id: 'abandoned_cart',
    title: 'Брошенные заявки',
    caption: 'Создали заявку или черновик, но заказ не завершён',
    icon: RefreshCw,
    color: 'emerald',
  },
  {
    id: 'no_orders',
    title: 'Новые без заказов',
    caption: 'Зарегистрированные клиенты, ещё не сделавшие заказ',
    icon: UserCheck,
    color: 'teal',
  },
  {
    id: 'regular',
    title: 'Постоянные клиенты',
    caption: '2 и более выполненных заказа в сервисе',
    icon: CheckCircle2,
    color: 'indigo',
  },
  {
    id: 'specific',
    title: 'Точечно по телефону',
    caption: 'Отправить пуш конкретному клиенту по номеру',
    icon: Phone,
    color: 'slate',
  },
];

const TEMPLATES = [
  {
    label: 'Скидка на повторный заказ',
    title: 'Скидка 15% на стирку и чистку!',
    body: 'Мы соскучились! Оформите заказ сегодня и получите приятную скидку 15% по промо-коду ВОЗВРАТ.',
    actionType: 'catalog',
  },
  {
    label: 'Бонусы ждут вас',
    title: 'У вас есть неиспользованные бонусы!',
    body: 'Оплатите до 30% стоимости следующего заказа накопленными баллами прямо сейчас.',
    actionType: 'catalog',
  },
  {
    label: 'Завершите оформление заявки',
    title: 'Ваша корзина сохранена',
    body: 'Вы не завершили оформление стирки. Заберите скидку 10% и отправьте заявку в 1 клик.',
    actionType: 'cart',
  },
  {
    label: 'Приветственная скидка 20%',
    title: 'Добро пожаловать в Большую Стирку!',
    body: 'Дарим 20% на первую стирку или химчистку. Курьер заберёт и вернёт чистые вещи!',
    actionType: 'catalog',
  },
  {
    label: 'Сезонная химчистка',
    title: 'Сезонная химчистка верхней одежды',
    body: 'Пора освежить пуховики, куртки и пальто со скидкой до конца недели.',
    actionType: 'catalog',
  },
];

const ClientCampaignManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Composer state
  const [segment, setSegment] = useState('all');
  const [targetPhone, setTargetPhone] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [actionType, setActionType] = useState('catalog');
  const [actionPayload, setActionPayload] = useState('');

  // Estimate state
  const [estimating, setEstimating] = useState(false);
  const [estimate, setEstimate] = useState(null);

  // Sending state
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [sending, setSending] = useState(false);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await clientAppRequest('/campaigns');
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || 'Ошибка загрузки истории рассылок');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  // Fetch estimate whenever segment or targetPhone changes
  const fetchEstimate = useCallback(async () => {
    if (segment === 'specific' && !targetPhone.trim()) {
      setEstimate(null);
      return;
    }
    setEstimating(true);
    try {
      const data = await clientAppRequest('/campaigns/estimate', {
        method: 'POST',
        body: JSON.stringify({
          segment,
          target_phone: segment === 'specific' ? targetPhone.trim() : null,
        }),
      });
      setEstimate(data);
    } catch (_) {
      setEstimate(null);
    } finally {
      setEstimating(false);
    }
  }, [segment, targetPhone]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchEstimate();
    }, 300);
    return () => clearTimeout(timer);
  }, [fetchEstimate]);

  const applyTemplate = (tpl) => {
    setTitle(tpl.title);
    setBody(tpl.body);
    setActionType(tpl.actionType);
  };

  const handleSendCampaign = async () => {
    if (!title.trim() || !body.trim()) {
      setError('Заполните заголовок и текст уведомления');
      return;
    }
    if (segment === 'specific' && !targetPhone.trim()) {
      setError('Укажите номер телефона получателя');
      return;
    }
    setSending(true);
    setError('');
    setNotice('');
    try {
      const result = await clientAppRequest('/campaigns/send', {
        method: 'POST',
        body: JSON.stringify({
          title: title.trim(),
          body: body.trim(),
          segment,
          target_phone: segment === 'specific' ? targetPhone.trim() : null,
          action_type: actionType,
          action_payload: actionPayload.trim() || null,
        }),
      });
      setConfirmModalOpen(false);
      setNotice(
        `Рассылка успешно отправлена! Получателей: ${result.recipients_count}, доставлено пушей: ${result.delivered_count}`
      );
      setTitle('');
      setBody('');
      await loadCampaigns();
      await fetchEstimate();
    } catch (err) {
      setError(err.message || 'Ошибка отправки рассылки');
      setConfirmModalOpen(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header bar */}
      <div className="flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-base font-bold text-gray-900 dark:text-white">
            <Bell className="h-5 w-5 text-blue-600" />
            Пуш-уведомления и рассылки клиентам
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Отправляйте точечные и массовые Web Push уведомления клиентам по
            продуманным сегментам: отток, брошенные заявки, бонусы, новые
            клиенты.
          </p>
        </div>
        <button
          type="button"
          onClick={loadCampaigns}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
          />
          Обновить историю
        </button>
      </div>

      {/* Status messages */}
      {(error || notice) && (
        <div
          className={`flex items-center justify-between rounded-xl border px-4 py-3 text-sm ${
            error
              ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/30 dark:text-red-200'
              : 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200'
          }`}
        >
          <div className="flex items-center gap-2">
            {error ? (
              <AlertCircle className="h-4 w-4 shrink-0" />
            ) : (
              <CheckCircle2 className="h-4 w-4 shrink-0" />
            )}
            <span>{error || notice}</span>
          </div>
          <button
            type="button"
            onClick={() => {
              setError('');
              setNotice('');
            }}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main Composer Grid */}
      <div className="grid gap-4 lg:grid-cols-12">
        {/* Left column: Segments & Form (8 cols) */}
        <div className="space-y-4 lg:col-span-7 xl:col-span-8">
          {/* 1. Step: Select Segment */}
          <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              1. Выберите сегмент аудитории
            </h3>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
              {SEGMENTS.map((s) => {
                const Icon = s.icon;
                const active = segment === s.id;
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => setSegment(s.id)}
                    className={`flex flex-col items-start rounded-xl border p-3 text-left transition ${
                      active
                        ? 'border-blue-500 bg-blue-50/80 shadow-sm dark:border-blue-600 dark:bg-blue-950/30'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50/50 dark:border-gray-800 dark:hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex w-full items-center justify-between mb-1">
                      <span
                        className={`flex h-7 w-7 items-center justify-center rounded-lg ${
                          active
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5" />
                      </span>
                      {active && (
                        <span className="h-2 w-2 rounded-full bg-blue-600" />
                      )}
                    </div>
                    <span className="text-xs font-bold text-gray-900 dark:text-white">
                      {s.title}
                    </span>
                    <span className="mt-0.5 text-[11px] text-gray-500 dark:text-gray-400 line-clamp-2">
                      {s.caption}
                    </span>
                  </button>
                );
              })}
            </div>

            {/* Target phone input if specific */}
            {segment === 'specific' && (
              <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50/50 p-3 dark:border-blue-900/50 dark:bg-blue-950/20">
                <label className="block text-xs font-semibold text-blue-900 dark:text-blue-200">
                  Номер телефона клиента:
                  <input
                    type="tel"
                    value={targetPhone}
                    onChange={(e) => setTargetPhone(e.target.value)}
                    placeholder="+7 (999) 123-45-67"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </label>
              </div>
            )}
          </div>

          {/* 2. Step: Message Composer & Quick Templates */}
          <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                2. Текст уведомления и шаблоны
              </h3>
            </div>

            {/* Template chips */}
            <div className="mb-3 flex flex-wrap gap-1.5">
              {TEMPLATES.map((tpl, idx) => (
                <button
                  type="button"
                  key={idx}
                  onClick={() => applyTemplate(tpl)}
                  className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1 text-[11px] font-medium text-gray-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 transition dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                >
                  ⚡ {tpl.label}
                </button>
              ))}
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Заголовок push-сообщения *
                  <input
                    type="text"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Например: Скидка 20% на первую стирку"
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                  />
                </label>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                  Текст сообщения *
                  <textarea
                    rows={3}
                    required
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Подробный текст, который клиент увидит на экране смартфона..."
                    className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white resize-none"
                  />
                </label>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                    Переход при клике на пуш
                    <select
                      value={actionType}
                      onChange={(e) => setActionType(e.target.value)}
                      className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                    >
                      <option value="catalog">Каталог услуг</option>
                      <option value="cart">Корзина / оформление</option>
                      <option value="orders">Мои заказы</option>
                      <option value="link">Ссылка</option>
                    </select>
                  </label>
                </div>

                <div>
                  {actionType === 'link' ? (
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">
                      URL ссылки
                      <input
                        type="text"
                        value={actionPayload}
                        onChange={(e) => setActionPayload(e.target.value)}
                        placeholder="https://... или /promo"
                        className="mt-1 w-full rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm outline-none focus:border-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
                      />
                    </label>
                  ) : (
                    <div className="pt-6 text-xs text-gray-400">
                      Откроет соответствующий раздел приложения
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right column: Live Push Mockup & Audience Stats (4-5 cols) */}
        <div className="space-y-4 lg:col-span-5 xl:col-span-4">
          {/* Audience Summary Card */}
          <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
              Аудитория рассылки
            </h3>

            {estimating ? (
              <div className="flex items-center gap-2 py-4 text-xs text-gray-500">
                <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                Расчёт сегмента…
              </div>
            ) : estimate ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <div className="rounded-xl bg-blue-50 p-3 dark:bg-blue-950/30">
                    <span className="block text-[11px] font-semibold text-blue-700 dark:text-blue-300">
                      Клиентов в сегменте
                    </span>
                    <span className="text-2xl font-black text-blue-950 dark:text-white">
                      {estimate.recipients_count}
                    </span>
                  </div>
                  <div className="rounded-xl bg-emerald-50 p-3 dark:bg-emerald-950/30">
                    <span className="block text-[11px] font-semibold text-emerald-700 dark:text-emerald-300">
                      Устройств с пушами
                    </span>
                    <span className="text-2xl font-black text-emerald-950 dark:text-white">
                      {estimate.push_subscribed_count}
                    </span>
                  </div>
                </div>

                {estimate.sample_clients?.length > 0 && (
                  <div>
                    <span className="block text-[11px] font-semibold text-gray-500 mb-1.5">
                      Примеры получателей (первые {estimate.sample_clients.length}):
                    </span>
                    <div className="max-h-40 overflow-y-auto space-y-1 rounded-xl border border-gray-100 p-2 dark:border-gray-800 text-xs">
                      {estimate.sample_clients.map((c, i) => (
                        <div
                          key={i}
                          className="flex items-center justify-between py-1 border-b border-gray-50 last:border-0 dark:border-gray-800/50"
                        >
                          <div>
                            <strong className="block text-gray-800 dark:text-gray-200">
                              {c.name}
                            </strong>
                            <span className="text-[11px] text-gray-500">
                              {c.phone}
                            </span>
                          </div>
                          <div className="text-right text-[11px] text-gray-500">
                            <span>Заказов: {c.total_orders}</span>
                            {c.bonus_balance > 0 && (
                              <span className="block text-violet-600 font-medium">
                                {c.bonus_balance} ₽
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-xs text-gray-400 py-3">
                Укажите параметры для расчёта аудитории
              </div>
            )}

            <button
              type="button"
              disabled={
                !title.trim() ||
                !body.trim() ||
                (segment === 'specific' && !targetPhone.trim())
              }
              onClick={() => setConfirmModalOpen(true)}
              className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-2.5 text-xs font-bold text-white shadow hover:bg-blue-700 disabled:opacity-40 transition"
            >
              <Send className="h-4 w-4" />
              Отправить рассылку
            </button>
          </div>

          {/* Smartphone Lockscreen Push Mockup */}
          <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
            <h3 className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-3">
              Предпросмотр уведомления на экране
            </h3>

            {/* Mock phone notification */}
            <div className="relative rounded-2xl border border-gray-200/80 bg-slate-900 p-4 text-white shadow-md">
              <div className="flex items-center justify-between text-[11px] text-gray-400 mb-2">
                <div className="flex items-center gap-1.5">
                  <div className="flex h-4 w-4 items-center justify-center rounded-md bg-blue-500 text-[10px] font-black text-white">
                    БС
                  </div>
                  <span className="font-semibold text-gray-200">
                    Большая Стирка
                  </span>
                </div>
                <span>сейчас</span>
              </div>

              <div className="pr-1">
                <h4 className="text-xs font-bold text-white leading-tight">
                  {title || 'Заголовок пуш-уведомления'}
                </h4>
                <p className="mt-1 text-xs text-gray-300 line-clamp-3">
                  {body ||
                    'Здесь будет отображаться текст сообщения, который привлечёт клиента сделать заказ.'}
                </p>
              </div>

              <div className="mt-3 flex items-center justify-between border-t border-gray-800 pt-2 text-[10px] text-gray-500">
                <span>Нажмите, чтобы открыть</span>
                <span className="text-blue-400 font-medium capitalize">
                  {actionType === 'catalog'
                    ? 'Каталог'
                    : actionType === 'cart'
                    ? 'Корзина'
                    : 'Заказы'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Campaign History Table */}
      <div className="rounded-2xl bg-white p-4 shadow-sm dark:bg-gray-900">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-bold text-gray-900 dark:text-white">
            <History className="h-4 w-4 text-blue-600" />
            История отправленных рассылок
          </div>
          <span className="text-xs text-gray-500">
            Всего: {campaigns.length}
          </span>
        </div>

        {loading ? (
          <div className="py-6 text-center text-xs text-gray-500">
            <Loader2 className="mx-auto h-5 w-5 animate-spin mb-1 text-blue-600" />
            Загрузка истории…
          </div>
        ) : campaigns.length === 0 ? (
          <div className="py-8 text-center text-xs text-gray-400">
            Рассылки ещё не отправлялись.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-100 text-[11px] font-semibold text-gray-400 dark:border-gray-800 uppercase tracking-wider">
                  <th className="py-2.5 px-3">Дата / Время</th>
                  <th className="py-2.5 px-3">Заголовок и текст</th>
                  <th className="py-2.5 px-3">Сегмент</th>
                  <th className="py-2.5 px-3">Получатели</th>
                  <th className="py-2.5 px-3">Пуши</th>
                  <th className="py-2.5 px-3">Отправитель</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 dark:divide-gray-800/60">
                {campaigns.map((c) => (
                  <tr
                    key={c.id}
                    className="hover:bg-gray-50/60 dark:hover:bg-gray-850/60 transition"
                  >
                    <td className="py-3 px-3 whitespace-nowrap text-gray-500 font-mono text-[11px]">
                      {c.created_at
                        ? new Date(c.created_at).toLocaleString('ru-RU', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })
                        : '—'}
                    </td>
                    <td className="py-3 px-3 max-w-xs sm:max-w-md">
                      <strong className="block text-gray-900 dark:text-white font-semibold">
                        {c.title}
                      </strong>
                      <span className="block text-gray-500 line-clamp-1 mt-0.5">
                        {c.body}
                      </span>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className="inline-block rounded-md bg-blue-50 px-2 py-0.5 text-[11px] font-semibold text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                        {SEGMENTS.find((s) => s.id === c.segment)?.title ||
                          c.segment}
                      </span>
                      {c.target_phone && (
                        <span className="block text-[10px] text-gray-400 font-mono mt-0.5">
                          {c.target_phone}
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap font-semibold text-gray-800 dark:text-gray-200">
                      {c.recipients_count}
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
                        {c.delivered_count} доставлено
                      </span>
                    </td>
                    <td className="py-3 px-3 whitespace-nowrap text-gray-500 text-[11px]">
                      {c.creator_name || 'Разработчик'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      {confirmModalOpen && (
        <ModalShell
          title="Подтверждение отправки рассылки"
          onClose={() => setConfirmModalOpen(false)}
          maxWidth="max-w-md"
        >
          <div className="space-y-3">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/30 dark:text-amber-200">
              Вы собираетесь отправить push-рассылку выбранному сегменту.
              Сообщение мгновенно появится на смартфонах клиентов.
            </div>

            <div className="rounded-xl border border-gray-100 bg-gray-50 p-3 text-xs space-y-1.5 dark:border-gray-800 dark:bg-gray-850">
              <div>
                <span className="text-gray-500">Сегмент: </span>
                <strong className="text-gray-900 dark:text-white">
                  {SEGMENTS.find((s) => s.id === segment)?.title || segment}
                </strong>
                {segment === 'specific' && targetPhone && (
                  <span className="font-mono text-blue-600 ml-1">
                    ({targetPhone})
                  </span>
                )}
              </div>
              <div>
                <span className="text-gray-500">Получателей: </span>
                <strong className="text-gray-900 dark:text-white">
                  {estimate?.recipients_count ?? '—'}
                </strong>
              </div>
              <div>
                <span className="text-gray-500">Заголовок: </span>
                <strong className="text-gray-900 dark:text-white">{title}</strong>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
              <button
                type="button"
                onClick={() => setConfirmModalOpen(false)}
                className="rounded-xl border border-gray-200 px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200"
              >
                Отмена
              </button>
              <button
                type="button"
                disabled={sending}
                onClick={handleSendCampaign}
                className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {sending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Подтвердить и отправить
              </button>
            </div>
          </div>
        </ModalShell>
      )}
    </div>
  );
};

export default ClientCampaignManager;
